import {useTranslation} from 'next-i18next'
import {Box, Button, Center, Container, Flex, Image as ChakraImage, Text,} from '@chakra-ui/react'
import {useQuery} from '@apollo/client'
import {useRouter} from '_app/hooks/useRouter'
import {addApolloState} from 'helpers/ApolloHelper/ApolloHelper'
import withAppInit from '_app/hoc/withAppInit'
import {Context} from '_app/types/context'
import Page from '_app/components/Page/Page'
import {GET_SHOWROOMS} from '_app/graphql/queries/content/GetShowrooms'
import Link from '_app/components/Link/Link'
import {getSeoSettings} from '_app/utils/localized/getSeoConfig'
import I18nHelper from '../../helpers/I18nHelper/I18nHelper'

type Props = {
  hasNoIndex: boolean;
  hasNoFollow: boolean;
};

const Showroom = ({ hasNoIndex, hasNoFollow }: Props) => {
  const { t } = useTranslation('boutique')
  const { data } = useQuery(GET_SHOWROOMS)
    const router = useRouter()
    const { locale } = router
    const { baseTitle } = getSeoSettings(locale, router)
  const query = { ...router.query }
  const showroom = data?.showrooms?.items?.find(showroom => showroom?.id === query?.id) ?? null
  const showroomAddress = showroom?.address.split(',')
  const [city, postcode, ...addressLine] = showroomAddress || []
  const getOpenHours = () => {
    const { monday, tuesday, wednesday, thursday, friday, saturday, sunday } =
      showroom?.open_hours
    if (monday === tuesday && monday === wednesday && monday === thursday && monday === friday) {
      return (
        <>
          <Text>{`${t('MONDAY', { ns: 'common' })} - ${t('FRIDAY', {
            ns: 'common',
          })}: ${monday}`}</Text>
          {saturday && (
            <Text>{`${t('SATURDAY', { ns: 'common' })}: ${saturday}`}</Text>
          )}
          {sunday && (
            <Text>{`${t('SUNDAY', { ns: 'common' })} ${sunday}`}</Text>
          )}
        </>
      )
    } else {
      return (
        <>
          <Text>{`${t('MONDAY', { ns: 'common' })}: ${monday}`}</Text>
          <Text>{`${t('TUESDAY', { ns: 'common' })}: ${tuesday}`}</Text>
          <Text>{`${t('WEDNESDAY', { ns: 'common' })}: ${wednesday}`}</Text>
          <Text>{`${t('THURSDAY', { ns: 'common' })}: ${thursday}`}</Text>
          <Text>{`${t('FRIDAY', { ns: 'common' })}: ${friday}`}</Text>
          <Text>{`${t('SATURDAY', { ns: 'common' })}: ${saturday}`}</Text>
          {sunday && (
            <Text>{`${t('SUNDAY', { ns: 'common' })} ${sunday}`}</Text>
          )}
        </>
      )
    }
  }
  return (
    <Page
      seo={{
        title: `${t('JEWELRY')} ${showroom?.name || ''} - ${baseTitle || ''}`,
      }}
      hasNoIndex={hasNoIndex}
      hasNoFollow={hasNoFollow}
    >
      <Container
        py={{
          base: '48px',
          lg: '72px',
        }}
        pt={{ base: '24px' }}
      >
        <Text textStyle="sectionHeader" as="h1" mb={8}>
          {`${t('JEWELRY')} ${showroom?.name || ''} - ${baseTitle || ''}`}
        </Text>
        <Flex
          flexDir={{ base: 'column', lg: 'row' }}
          gap={{
            base: '24px',
            lg: '150px',
          }}
          alignItems="flex-start"
        >
          <Box flex={1}>
            <Box position="relative" alignSelf="flex-start">
              <ChakraImage
                alt={`${showroom?.name} boutique`}
                src={showroom?.images[0] ?? '/images/showroom.webp'}
                width={{ base: '100%', lg: 690 }}
              />
            </Box>
          </Box>
          <Box
            flex={1}
            w={{
              base: '100%',
              lg: 'auto',
            }}
          >
            {!showroom ? (
              <Text my={12}>{t('WE_COULDNT_FIND_THE_BOUTIQUE')}</Text>
            ) : (
              <Center height="100%" justifyContent="flex-start">
                <Flex flex={1} flexDir="column">
                  <Box width="100%" borderTop="1px solid #dddddd" py={4}>
                    <Text mb={2}>{t('CONTACT', { ns: 'common' })}</Text>
                    <Box textStyle="bodyLight" lineHeight={1.38}>
                      {showroom?.contact?.phone && (
                        <Text
                          as="a"
                          display="block"
                          _hover={{
                            textDecoration: 'underline',
                          }}
                          href={`tel:${showroom?.contact?.phone}`}
                        >
                          {showroom?.contact?.phone}
                        </Text>
                      )}
                      {!!showroom?.contact?.email?.length && (
                        <>
                          {showroom?.contact?.email.map(email => (
                            <Text
                              as="a"
                              display="block"
                              _hover={{
                                textDecoration: 'underline',
                              }}
                              key={email}
                              href={`mailto:${email}`}
                            >
                              {email}
                            </Text>
                          ))}
                        </>
                      )}{' '}
                    </Box>
                  </Box>
                  <Box width="100%" borderTop="1px solid #dddddd" py={4}>
                    <Text mb={2}>{t('OPENING_HOURS')}</Text>
                    <Box textStyle="bodyLight" lineHeight={1.38}>
                      {getOpenHours()}
                    </Box>
                  </Box>
                  <Box
                    width="100%"
                    borderTop="1px solid #dddddd"
                    borderBottom="1px solid #dddddd"
                    py={4}
                    mb={6}
                  >
                    <Text mb={2}>{t('ADDRESS', { ns: 'common' })}</Text>
                    <Box textStyle="bodyLight" lineHeight={1.38}>
                      <Text>{addressLine.join(',')}</Text>
                      <Text>
                        {postcode} {city}
                      </Text>
                    </Box>
                  </Box>
                  <Link
                    href={`https://www.google.com/maps/search/?api=1&query=${showroom?.address}+lilou`}
                    passHref
                  >
                    <Button
                      maxW={{
                        base: '100%',
                        md: '210px',
                      }}
                      variant="outline"
                      disabled={!showroom?.address}
                    >
                      {t('CREATE_A_ROUTE')}
                    </Button>
                  </Link>
                </Flex>
              </Center>
            )}
          </Box>
        </Flex>
      </Container>
    </Page>
  )
}

export const getServerSideProps = withAppInit(async (ctx: Context) => {
  return addApolloState(ctx.apolloClient, {
    props: {
      ...(await I18nHelper.getI18nProps(ctx, ['boutique'])),
    },
  })
})

export default Showroom
